import React, { useEffect, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { useNavigate, useLocation } from "react-router-dom";
import * as turf from "@turf/turf";
import { Save } from 'lucide-react';

const SiteMapEdit = () => {
    const mapContainerRef = useRef();
    const mapRef = useRef();
    const drawRef = useRef();
    const navigate = useNavigate();
    const location = useLocation();
    const [polygonData, setPolygonData] = useState(null);
    const [area, setArea] = useState(0);
    const [mapStyle, setMapStyle] = useState("mapbox://styles/mapbox/satellite-streets-v11");

    const mapStyles = [
        { label: "Satellite", value: "mapbox://styles/mapbox/satellite-streets-v11" },
        { label: "Streets", value: "mapbox://styles/mapbox/streets-v12" },
        { label: "Outdoors", value: "mapbox://styles/mapbox/outdoors-v12" },
        { label: "Light", value: "mapbox://styles/mapbox/light-v11" },
        { label: "Dark", value: "mapbox://styles/mapbox/dark-v11" },
    ];

    // Ensure fullscreen map display
    useEffect(() => {
        // Store original values
        const originalBodyOverflow = document.body.style.overflow;
        const originalHtmlOverflow = document.documentElement.style.overflow;
        const originalBodyHeight = document.body.style.height;
        const originalRootHeight = document.getElementById('root')?.style.height;
        
        // Add class to body for CSS targeting
        document.body.classList.add('map-active');
        
        // Force clean styles
        document.body.style.overflow = 'hidden';
        document.body.style.margin = '0';
        document.body.style.padding = '0';
        document.body.style.height = '100vh';
        document.body.style.position = 'fixed';
        document.body.style.width = '100vw';
        document.documentElement.style.overflow = 'hidden';
        document.documentElement.style.margin = '0';
        document.documentElement.style.padding = '0';
        document.documentElement.style.height = '100vh';
        
        // Ensure #root doesn't constrain
        const rootEl = document.getElementById('root');
        if (rootEl) {
            rootEl.style.height = '100vh';
            rootEl.style.overflow = 'hidden';
        }
        
        // Cleanup on unmount
        return () => {
            document.body.classList.remove('map-active');
            document.body.style.overflow = originalBodyOverflow;
            document.body.style.height = originalBodyHeight;
            document.body.style.position = '';
            document.body.style.width = '';
            document.documentElement.style.overflow = originalHtmlOverflow;
            document.documentElement.style.height = '';
            if (rootEl) {
                rootEl.style.height = originalRootHeight || '';
                rootEl.style.overflow = '';
            }
        };
    }, []);

    useEffect(() => {
        mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

        mapRef.current = new mapboxgl.Map({
            container: mapContainerRef.current,
            style: mapStyle,
            center: [76.9539, 11.0161],
            zoom: 17,
        });

        drawRef.current = new MapboxDraw({
            displayControlsDefault: false,
            controls: { polygon: true, trash: true },
            defaultMode: "draw_polygon",
        });

        mapRef.current.addControl(drawRef.current);

        // Get geometry from route state or localStorage
        const siteFromState = location.state?.site;
        let geometryToLoad = null;

        if (siteFromState && siteFromState.geometry) {
            // Parse the geometry from the site
            try {
                geometryToLoad = typeof siteFromState.geometry === 'string' 
                    ? JSON.parse(siteFromState.geometry) 
                    : siteFromState.geometry;
                    
                // Handle FeatureCollection
                if (geometryToLoad.type === 'FeatureCollection') {
                    geometryToLoad = geometryToLoad.features[0].geometry;
                }
            } catch (e) {
                console.error('Error parsing geometry:', e);
            }
        }

        if (!geometryToLoad) {
            const storedPolygon = localStorage.getItem("selectedPolygon");
            if (storedPolygon) {
                geometryToLoad = JSON.parse(storedPolygon);
            }
        }

        if (geometryToLoad) {
            const addedIds = []; // keep track of feature ids we add

            if (geometryToLoad.type === "MultiPolygon") {
                geometryToLoad.coordinates.forEach((polygonCoords, index) => {
                    const id = `polygon-${index}`;
                    drawRef.current.add({
                        id,
                        type: "Feature",
                        properties: {},
                        geometry: { type: "Polygon", coordinates: polygonCoords },
                    });
                    addedIds.push(id);
                });
            } else if (geometryToLoad.type === "Polygon") {
                const id = "editablePolygon";
                drawRef.current.add({
                    id,
                    type: "Feature",
                    properties: {},
                    geometry: geometryToLoad,
                });
                addedIds.push(id);
            }

            // 👉 Immediately select all added features so they are editable
            if (addedIds.length > 0) {
                drawRef.current.changeMode("simple_select", { featureIds: addedIds });
            }

            // Fly to polygon
            const featureCollection = {
                type: "FeatureCollection",
                features: [
                    {
                        type: "Feature",
                        geometry: geometryToLoad,
                        properties: {},
                    },
                ],
            };

            mapRef.current.flyTo({
                center: turf.center(featureCollection).geometry.coordinates,
                zoom: 15,
                duration: 1000,
            });

            const allFeatures = drawRef.current.getAll();
            const calculatedArea = turf.area(allFeatures);
            setArea(calculatedArea.toFixed(2));
        }

        mapRef.current.on("draw.update", updatePolygon);
        mapRef.current.on("draw.delete", () => {
            setPolygonData(null);
            setArea(0);
        });

        // Force map to resize after DOM settles
        mapRef.current.on('load', () => {
            requestAnimationFrame(() => {
                if (mapRef.current) {
                    mapRef.current.resize();
                }
            });
        });

        return () => {
            if (mapRef.current) mapRef.current.remove();
        };
    }, [mapStyle]);

    const updatePolygon = () => {
        const data = drawRef.current.getAll();
        if (data.features.length > 0) {
            const allPolygons = data.features.map((f) => f.geometry);
            const combinedGeometry =
                allPolygons.length === 1
                    ? allPolygons[0]
                    : {
                        type: "MultiPolygon",
                        coordinates: allPolygons.map((g) => g.coordinates),
                    };
            const calculatedArea = turf.area(data);
            setPolygonData(combinedGeometry);
            setArea(calculatedArea.toFixed(2));
        }
    };

    const savePolygon = () => {
        if (polygonData) {
            localStorage.setItem("selectedPolygon", JSON.stringify(polygonData));
            localStorage.setItem("polygonArea", area);
            navigate(-1);
        } else {
            alert("No polygon to save.");
        }
    };

    const handleStyleChange = (e) => setMapStyle(e.target.value);
    const handleClose = () => navigate(-1);

    return (
        <div style={{ 
            height: "100vh",
            width: "100vw",
            position: "fixed",
            top: 0,
            left: 0,
            margin: 0,
            padding: 0,
            overflow: "hidden",
            zIndex: 9999
        }}>
            <div ref={mapContainerRef} style={{ height: "100vh", width: "100vw" }} />

            {/* Area + Save button */}
            <div
                style={{
                    position: "absolute",
                    top: 20,
                    left: 20,
                    background: "linear-gradient(135deg, #22c45d 0%, #1a9849 100%)",
                    padding: "16px 20px",
                    borderRadius: "12px",
                    boxShadow: "0 8px 24px rgba(34, 196, 93, 0.3)",
                    fontFamily: "'Public Sans', sans-serif",
                    color: "white",
                    minWidth: "280px"
                }}
            >
                <div style={{ 
                    display: "flex", 
                    alignItems: "center", 
                    gap: "8px",
                    marginBottom: "12px",
                    fontSize: "16px",
                    fontWeight: 600
                }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                    Edit Site Boundary
                </div>
                {area > 0 && (
                    <p style={{ 
                        margin: "0 0 12px 0", 
                        fontSize: "15px",
                        fontWeight: 500,
                        background: "rgba(255,255,255,0.15)",
                        padding: "8px 12px",
                        borderRadius: "6px",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px"
                    }}>
                        <span style={{ fontSize: "18px" }}>⬡</span> Area: {area} sq. meters
                    </p>
                )}
                <button
                    onClick={savePolygon}
                    disabled={!polygonData}
                    style={{
                        backgroundColor: polygonData ? "white" : "rgba(255,255,255,0.3)",
                        color: polygonData ? "#22c45d" : "rgba(255,255,255,0.6)",
                        border: "none",
                        padding: "10px 20px",
                        borderRadius: "8px",
                        cursor: polygonData ? "pointer" : "not-allowed",
                        fontSize: "14px",
                        fontWeight: 600,
                        width: "100%",
                        transition: "all 0.2s",
                        boxShadow: polygonData ? "0 4px 12px rgba(0,0,0,0.1)" : "none",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px"
                    }}
                    onMouseOver={(e) => {
                        if (polygonData) {
                            e.currentTarget.style.transform = "translateY(-2px)";
                            e.currentTarget.style.boxShadow = "0 6px 16px rgba(0,0,0,0.15)";
                        }
                    }}
                    onMouseOut={(e) => {
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = polygonData ? "0 4px 12px rgba(0,0,0,0.1)" : "none";
                    }}
                >
                    <Save size={18} />
                    Save Changes
                </button>
            </div>

            {/* Style switcher */}
            <div
                style={{
                    position: "absolute",
                    top: 20,
                    right: 80,
                    background: "white",
                    padding: "12px 16px",
                    borderRadius: "12px",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    fontFamily: "'Public Sans', sans-serif",
                }}
            >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22c45d" strokeWidth="2">
                    <path d="M12 2v6m0 4v10M6 8l-4 4 4 4m12-8l4 4-4 4"></path>
                </svg>
                <select
                    value={mapStyle}
                    onChange={handleStyleChange}
                    style={{
                        padding: "8px 12px",
                        borderRadius: "8px",
                        border: "1.5px solid #e5e7eb",
                        fontSize: "14px",
                        fontWeight: 500,
                        cursor: "pointer",
                        outline: "none",
                        background: "white",
                        color: "#1a2333"
                    }}
                >
                    {mapStyles.map((style) => (
                        <option key={style.value} value={style.value}>
                            {style.label}
                        </option>
                    ))}
                </select>
            </div>

            <button
                onClick={handleClose}
                style={{
                    backgroundColor: "#ef4444",
                    color: "#fff",
                    border: "none",
                    padding: "12px",
                    borderRadius: "12px",
                    cursor: "pointer",
                    fontSize: "20px",
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "48px",
                    height: "48px",
                    transition: "all 0.2s",
                    position: "absolute",
                    top: 20,
                    right: 20,
                    boxShadow: "0 4px 16px rgba(239, 68, 68, 0.3)"
                }}
                onMouseOver={(e) => {
                    e.target.style.backgroundColor = "#dc2626";
                    e.target.style.transform = "scale(1.05)";
                    e.target.style.boxShadow = "0 6px 20px rgba(239, 68, 68, 0.4)";
                }}
                onMouseOut={(e) => {
                    e.target.style.backgroundColor = "#ef4444";
                    e.target.style.transform = "scale(1)";
                    e.target.style.boxShadow = "0 4px 16px rgba(239, 68, 68, 0.3)";
                }}
                title="Close"
            >
                ✕
            </button>
        </div>
    );
};

export default SiteMapEdit;
